<script setup>
import { reactive, computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength, requiredIf, url } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

const props = defineProps({
  knowledgeBaseId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const MAX_FILE_SIZE = 80 * 1024 * 1024; // 80MB

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('pilotDocuments/getUIFlags'),
};

const initialState = {
  name: '',
  url: '',
  documentType: 0, // 0 = URL, 1 = 文件上传
  pdfFile: null,
  pdfFiles: [], // 支持多文件上传
  crawlLinks: false,

};

const state = reactive({ ...initialState });
const fileInputRef = ref(null);

// TabBar 标签定义
const documentTabs = computed(() => [
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_TYPE_URL') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_TYPE_FILE') },
]);

const validationRules = {
  url: {
    required: requiredIf(() => state.documentType === 0),
    url: requiredIf(() => state.documentType === 0 && url),
    minLength: requiredIf(() => state.documentType === 0 && minLength(1)),
  },
  pdfFile: {
    required: requiredIf(() => state.documentType === 1),
  },
};

// 处理标签切换
const handleTabChanged = tab => {
  state.documentType = documentTabs.value.indexOf(tab);
  // 清空文件选择当切换到 URL 模式
  if (state.documentType === 0) {
    state.pdfFiles = [];
    state.pdfFile = null;
  }
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const hasPdfFileError = computed(() => v$.value.pdfFile.$error);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_${errorKey}_ERROR`)
    : '';
};

const formErrors = computed(() => ({
  url: getErrorMessage('url', 'URL'),
  pdfFile: getErrorMessage('pdfFile', 'PDF_FILE'),
}));

const handleCancel = () => emit('cancel');

const handleFileChange = event => {
  const files = Array.from(event.target.files);
  if (files.length === 0) return;

  // 检查文件数量限制（最多 10 个）
  if (files.length > 10) {
    useAlert('每次最多上传 10 个文件');
    event.target.value = '';
    return;
  }

  // 支持的文件类型
  const allowedContentTypes = [
    'application/pdf', // PDF
    'text/plain', // TXT
    'text/markdown', // MD
    'text/x-markdown', // MD (另一种 MIME type)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
    'application/msword', // DOC (旧版)
    'application/vnd.ms-powerpoint', // PPT (旧版)
  ];

  // 验证每个文件
  const validFiles = [];
  for (const file of files) {
    // 检查文件类型（通过 MIME type 或扩展名）
    const fileName = file.name.toLowerCase();
    const hasValidExtension = /\.(pdf|txt|md|markdown|docx|ppt|pptx)$/i.test(
      fileName
    );
    const isValidContentType = allowedContentTypes.includes(file.type);

    if (!isValidContentType && !hasValidExtension) {
      useAlert(`不支持的文件格式：${file.name}`);
      event.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      useAlert(`文件 ${file.name} 超过 80MB 限制`);
      event.target.value = '';
      return;
    }

    validFiles.push(file);
  }

  state.pdfFiles = validFiles;
  // 如果只有一个文件，设置 pdfFile（用于表单验证）
  if (validFiles.length === 1) {
    state.pdfFile = validFiles[0];
  } else {
    state.pdfFile = null;
  }
};

const openFileDialog = () => {
  // Use nextTick to ensure the ref is available
  nextTick(() => {
    if (fileInputRef.value) {
      fileInputRef.value.click();
    }
  });
};

const prepareDocumentDetails = () => {
  const formData = new FormData();
  formData.append('document[knowledge_base_id]', props.knowledgeBaseId);

  if (state.documentType === 0) {
    // URL 模式
    formData.append('document[external_link]', state.url);
    // 不发送 name，让后端从网页 title 自动获取
    formData.append('document[crawl_links]', state.crawlLinks);
  } else {
    // 文件上传模式
    formData.append('document[pdf_file]', state.pdfFile);
    // 不发送 name，让后端从文件名自动获取
    // No need to send external_link for file uploads - it's auto-generated in the backend
  }

  return formData;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  // 如果有多个文件，循环提交每个文件
  if (state.pdfFiles.length > 1) {
    for (const file of state.pdfFiles) {
      const singleFormData = prepareSingleDocumentDetails(file);
      emit('submit', singleFormData);
    }
    // 清空选择
    state.pdfFiles = [];
    state.pdfFile = null;
  } else {
    // 单个文件或 URL
    emit('submit', prepareDocumentDetails());
  }
};

// 准备单个文件的文档详情
const prepareSingleDocumentDetails = file => {
  const formData = new FormData();
  formData.append('document[knowledge_base_id]', props.knowledgeBaseId);

  // 文件上传模式
  formData.append('document[pdf_file]', file);
  // 不发送 name，让后端从文件名自动获取

  return formData;
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- TabBar 切换 -->
    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_TYPE_LABEL') }}
      </label>
      <TabBar
        :tabs="documentTabs"
        :initial-active-tab="state.documentType"
        @tab-changed="handleTabChanged"
      />
    </div>

    <Input
      v-if="state.documentType === 0"
      v-model="state.url"
      :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_URL_LABEL')"
      :placeholder="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_URL_PLACEHOLDER')"
      :message="formErrors.url"
      :message-type="formErrors.url ? 'error' : 'info'"
    />

    <div v-if="state.documentType === 0" class="flex flex-col gap-1 mt-2">
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          v-model="state.crawlLinks"
          type="checkbox"
          class="w-4 h-4 rounded border-n-slate-3 text-n-slate-12 focus:ring-n-slate-12"
        />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_CRAWL_LABEL') }}
        </span>
      </label>
      <p class="ml-6 text-xs text-n-slate-11">
        {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_CRAWL_HINT') }}
      </p>
    </div>

    <div v-if="state.documentType === 1" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_PDF_LABEL') }}
      </label>
      <div class="relative">
        <input
          ref="fileInputRef"
          type="file"
          accept=".pdf,.txt,.md,.markdown,.docx,.ppt,.pptx"
          multiple
          class="hidden"
          @change="handleFileChange"
        />
        <Button
          type="button"
          :color="hasPdfFileError ? 'ruby' : 'slate'"
          :variant="hasPdfFileError ? 'outline' : 'solid'"
          class="!w-full !h-auto !justify-between !py-4"
          @click="openFileDialog"
        >
          <template #default>
            <div class="flex gap-2 items-center">
              <div
                class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-slate-3"
              >
                <i class="text-xl i-ph-file-pdf text-n-slate-11" />
              </div>
              <div class="flex flex-col flex-1 gap-1 items-start">
                <p class="m-0 text-sm font-medium text-n-slate-12">
                  {{
                    state.pdfFiles.length > 0
                      ? `已选择 ${state.pdfFiles.length} 个文件`
                      : t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_PDF_BUTTON')
                  }}
                </p>
                <p class="m-0 text-xs text-n-slate-11">
                  {{
                    state.pdfFiles.length > 0
                      ? state.pdfFiles.map(f => f.name).join(', ')
                      : t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_PDF_HELP')
                  }}
                </p>
              </div>
            </div>

            <i class="i-lucide-upload text-n-slate-11" />
          </template>
        </Button>
      </div>
      <p v-if="formErrors.pdfFile" class="text-xs text-n-ruby-9">
        {{ formErrors.pdfFile }}
      </p>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('PILOT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('PILOT.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
